// src/pages/UserManagementPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import * as yup from "yup";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, KeyRound, SquarePen, UserLock } from "lucide-react";
import DataTable from "@/components/common/DataTable";
import ReactSelect from "@/components/common/ReactSelect";
import useDebounce from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/formatDateTime";
import { usePermissions } from "@/context/PermissionContext";
import { toast } from "sonner";
import PhoneInputComponent from "@/components/common/PhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";

// ── Validation Schemas ─────────────────────────────────────────────────────
const userSchema = yup.object().shape({
  username: yup.string().required("Username is required").min(3, "Minimum 3 characters").max(50, "Maximum 50 characters"),
  full_name: yup.string().required("Full name is required").min(2, "Minimum 2 characters").max(100, "Maximum 100 characters"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  phone: yup.string().required("Phone number is required").test("phone-valid", "Please enter a valid phone number", (val) => {
    if (!val) return false;
    try { return isValidPhoneNumber(val); } catch { return false; }
  }),
  password: yup.string().when("$isCreate", {
    is: true,
    then: (s) => s.required("Password is required for new user").min(6, "Minimum 6 characters"),
    otherwise: (s) => s.nullable(),
  }),
  role_id: yup.string().required("Role is required"),
  status: yup.string().oneOf(["active", "inactive"], "Please select valid status").when("$isCreate", {
    is: false,
    then: (s) => s.required("Status is required when updating"),
    otherwise: (s) => s.nullable(),
  }),
});

const passwordChangeSchema = yup.object().shape({
  new_password: yup.string().required("New password is required").min(6, "Password must be at least 6 characters"),
  confirm_password: yup.string().required("Please confirm your password").oneOf([yup.ref("new_password")], "Passwords do not match"),
});

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // ── State ──────────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 20, totalRecords: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ new_password: "", confirm_password: "" });
  const [passwordErrors, setPasswordErrors] = useState({});

  const [formData, setFormData] = useState({ username: "", full_name: "", email: "", phone: "", password: "", role_id: "", status: "active" });
  const [errors, setErrors] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const hasMounted = useRef(false);
  const originalData = useRef({});  // ← add karo


  // ── API ────────────────────────────────────────────────────────────────────
  const fetchUsers = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);
      let url = `/get-all-users?page=${page}&perPage=${perPage}`;
      if (sortBy && sortOrder) url += `&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (debouncedSearch.trim()) url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      const res = await apiCall("GET", url, null, navigate);
      if (res?.success) {
        setUsers(res.data || []);
        setPagination(res.pagination);
      }
    } catch (err) { console.error("Failed to load users:", err); }
    finally { setLoading(false); }
  };

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const res = await apiCall("GET", "/get-all-roles", null, navigate);
      if (res?.success) setRoles(res.data.roles || []);
    } catch (err) { console.error("Failed to load roles:", err); }
    finally { setRolesLoading(false); }
  };

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    fetchUsers(1);
    fetchRoles();
  }, []);

  useEffect(() => {
    if (!hasMounted.current) return;
     fetchUsers(1);
  }, [debouncedSearch, sortBy, sortOrder]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) setSortOrder((p) => (p === "ASC" ? "DESC" : "ASC"));
    else { setSortBy(col); setSortOrder("ASC"); }
  };

  const resetForm = () => { setFormData({ username: "", full_name: "", email: "", phone: "", password: "", role_id: "", status: "active" }); setEditMode(false); setSelectedUserId(null); setErrors({}); setShowPassword(false); };
  const resetPasswordForm = () => { setPasswordForm({ new_password: "", confirm_password: "" }); setPasswordErrors({}); setShowNewPassword(false); setShowConfirmPassword(false); };

  const handleOpenDialog = (user = null) => {
  if (user) {
    const data = {
      username: user.username || "",
      full_name: user.full_name || "",
      email: user.email || "",
      phone: user.phone || "",
      password: "",
      role_id: user.role_id?.toString() || "",
      status: user.status || "active",
    };
    setEditMode(true);
    setSelectedUserId(user.user_id);
    setFormData(data);
    originalData.current = data;
  } else {
    resetForm();
    originalData.current = {};
  }
  setDialogOpen(true);
};

  const handleOpenPasswordDialog = (user) => {
    setSelectedUserId(user.user_id);
    setSelectedUserName(user.full_name || user.username);
    resetPasswordForm();
    setPasswordDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setTimeout(resetForm, 200); };
  const handleClosePasswordDialog = () => { setPasswordDialogOpen(false); setTimeout(resetPasswordForm, 200); };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((p) => ({ ...p, [name]: value }));
    if (passwordErrors[name]) setPasswordErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    try {
      const dataToValidate = { ...formData };
      if (editMode && !dataToValidate.password?.trim()) delete dataToValidate.password;
      await userSchema.validate(dataToValidate, { abortEarly: false, context: { isCreate: !editMode } });
      setSubmitting(true);
      const payload = { role_id: Number(formData.role_id), username: formData.username.trim(), full_name: formData.full_name.trim(), email: formData.email.trim(), phone: formData.phone.trim() || null };
      if (editMode) payload.status = formData.status;
      if (!editMode || formData.password?.trim()) payload.password = formData.password.trim();
      if (editMode && selectedUserId) await apiCall("POST", `/update-user/${selectedUserId}`, payload, navigate);
      else await apiCall("POST", "/create-user", payload, navigate);
      handleCloseDialog();
      fetchUsers(pagination.currentPage, pagination.perPage);
    } catch (err) {
      if (err.name === "ValidationError") { const e = {}; err.inner.forEach((er) => { e[er.path] = er.message; }); setErrors(e); }
      else console.error("Submit error:", err);
    } finally { setSubmitting(false); }
  };

  const handlePasswordChange = async () => {
    try {
      await passwordChangeSchema.validate(passwordForm, { abortEarly: false });
      setSubmitting(true);
      await apiCall("PUT", "/users/change-password", { user_id: selectedUserId.toString(), new_password: passwordForm.new_password }, navigate);
      
      // ❌ Yeh line hata do — apiCall already toast show kar raha hai
      // toast.success("Password changed successfully");
      
      handleClosePasswordDialog();
    } catch (err) {
      if (err.name === "ValidationError") { 
        const e = {}; 
        err.inner.forEach((er) => { e[er.path] = er.message; }); 
        setPasswordErrors(e); 
      }
      // ❌ Yeh bhi hata do
      // else toast.error("Failed to change password");
    } finally { setSubmitting(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserId) return;
    try {
      setSubmitting(true);
      await apiCall("POST", `/delete-user/${selectedUserId}`, null, navigate);
      setDeleteDialogOpen(false);
      fetchUsers(pagination.currentPage, pagination.perPage);
    } catch (err) { console.error("Delete failed:", err); }
    finally { setSubmitting(false); setSelectedUserId(null); }
  };

  // ── DataTable config ───────────────────────────────────────────────────────
  const columns = [
    {
      key: "full_name",
      label: "Full Name",
      sortable: true,
      render: (row) => <div className="font-medium">{row.full_name}</div>,
    },
    { key: "username", label: "Username", sortable: true },
    { key: "email", label: "Email", sortable: true },
    {
      key: "phone",
      label: "Phone",
      render: (row) => row.phone || "—",
    },
    {
      key: "role_name",
      label: "Role",
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-secondary text-secondary-foreground">
          {row.role_name}
        </span>
      ),
    },
    {
      key: "last_login",
      label: "Last Login",
      sortable: true,
      render: (row) => (
        <span className="text-muted-foreground text-sm">
          {row.last_login ? formatDateTime(row.last_login) : "Never"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${row.status === "active" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"}`}>
          {row.status}
        </span>
      ),
    },
  ];

  const rowActions = (row) => [
  {
    label: "Edit",
    icon: (
      <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#DBFCE780] text-[#407E3B] dark:bg-[#DBFCE715] dark:text-[#6DBF67]">
        <SquarePen className="h-4 w-4" />
      </span>
    ),
    onClick: () => handleOpenDialog(row),
    hidden: !hasPermission("users_edit"),
  },
  {
    label: "Change Password",
    icon: (
      <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#E8E8E880] text-[#6C7278] dark:bg-[#FAE6EC15] dark:text-[#6C7278]">
        <UserLock className="h-4 w-4" />
      </span>
    ),
    onClick: () => handleOpenPasswordDialog(row),
  },
  {
    label: "Delete",
    icon: (
      <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#FAE6EC80] text-[#D3663D] dark:bg-[#FAE6EC15] dark:text-[#F28B6E]">
        <Trash2 className="h-4 w-4" />
      </span>
    ),
    onClick: () => { setSelectedUserId(row.user_id); setDeleteDialogOpen(true); },
    className: "text-destructive focus:text-destructive",
    hidden: !hasPermission("users_delete"),
  },
];

 const isFormDirty = editMode
    ? JSON.stringify(formData) !== JSON.stringify(originalData.current)
    : true;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DataTable
        title="User Management"
        description="Create, update and manage system users"
        headerActions={
          hasPermission("users_create") && (
            <Button onClick={() => handleOpenDialog()} className="gap-2 w-full sm:w-auto h-9 text-sm">
              <Plus className="h-4 w-4" /> Add User
            </Button>
          )
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search users..."
        columns={columns}
        rows={users}
        rowKey="user_id"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        pagination={pagination}
        onPageChange={(p) => fetchUsers(p, pagination.perPage)}
        onPerPageChange={(pp) => fetchUsers(1, Number(pp))}
        paginationTitle="users"
        loading={loading}
        emptyMessage="No users found. Create your first user to get started."
        rowActions={rowActions}
      />

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="w-[95vw] max-w-[560px] lg:min-w-[700px] lg:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit User" : "Create New User"}</DialogTitle>
            <DialogDescription>{editMode ? "Update user details below." : "Fill in the details to create a new user."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="grid gap-1.5">
                <Label htmlFor="full_name">Full Name <span className="text-destructive">*</span></Label>
                <Input id="full_name" name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder="Enter full name" className={errors.full_name ? "border-destructive" : ""}  autoComplete="off" />
                {errors.full_name && <p className="text-xs text-destructive">{errors.full_name}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="username">Username <span className="text-destructive">*</span></Label>
                <Input id="username" name="username" value={formData.username} onChange={handleInputChange} placeholder="Enter username" className={errors.username ? "border-destructive" : ""}  autoComplete="off" />
                {errors.username && <p className="text-xs text-destructive">{errors.username}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Enter email" className={`autofill-fix ${errors.email ? "border-destructive" : ""}`}  autoComplete="off" />
                {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              </div>
              {/* <div className="grid gap-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Enter phone number" />
              </div> */}
             
             <div className="grid gap-1.5">
  <Label htmlFor="phone">Phone <span className="text-destructive">*</span></Label>
  <PhoneInputComponent
    id="phone"
    name="phone"
    value={formData.phone}
    onChange={(phone) => {
      setFormData((prev) => ({ ...prev, phone: phone || "" }));
      // Clear error when user types
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: "" }));
      }
    }}
    onValidationChange={(isValid, phoneValue) => {
      if (!isValid && phoneValue) {
        setErrors((prev) => ({ ...prev, phone: "Please enter a valid phone number" }));
      } else {
        setErrors((prev) => ({ ...prev, phone: "" }));
      }
    }}
    error={errors.phone}
    defaultCountry="SA"
  />
</div>
              <div className="grid gap-1.5">
                <Label htmlFor="role_id">Role <span className="text-destructive">*</span></Label>
                {rolesLoading ? (
                  <div className="text-sm text-muted-foreground">Loading roles...</div>
                ) : (
                  <ReactSelect
                    options={roles.map((r) => ({ value: r.role_id.toString(), label: r.role_name }))}
                    value={roles.map((r) => ({ value: r.role_id.toString(), label: r.role_name })).find((o) => o.value === formData.role_id) || null}
                    onChange={(s) => { setFormData((p) => ({ ...p, role_id: s?.value || "" })); if (errors.role_id) setErrors((p) => ({ ...p, role_id: "" })); }}
                    placeholder="Select role"
                    isDisabled={submitting}
                  />
                )}
                {errors.role_id && <p className="text-xs text-destructive">{errors.role_id}</p>}
              </div>
              {editMode && (
                <div className="grid gap-1.5">
                  <Label htmlFor="status">Status <span className="text-destructive">*</span></Label>
                  <ReactSelect
                    options={statusOptions}
                    value={statusOptions.find((o) => o.value === formData.status) || null}
                    onChange={(s) => { setFormData((p) => ({ ...p, status: s?.value || "active" })); if (errors.status) setErrors((p) => ({ ...p, status: "" })); }}
                    placeholder="Select status"
                    isDisabled={submitting}
                  />
                  {errors.status && <p className="text-xs text-destructive">{errors.status}</p>}
                </div>
              )}
              {!editMode && (
                <div className="grid gap-1.5">
                  <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Input id="password" name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleInputChange} placeholder="Enter password" className={`autofill-fix pr-10 ${errors.password ? "border-destructive" : ""}`}  autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none" tabIndex={-1}>
                     {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>
              )}
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={submitting || !isFormDirty} className="w-full sm:w-auto">
  {submitting ? (
    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{editMode ? "Updating..." : "Creating..."}</>
  ) : editMode ? "Update User" : "Create User"}
</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Change Password Dialog ── */}
      <Dialog open={passwordDialogOpen} onOpenChange={handleClosePasswordDialog}>
        <DialogContent className="w-[95vw] max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Change password for <span className="font-semibold">{selectedUserName}</span></DialogDescription>
          </DialogHeader>
          {/* Own <form> so the browser doesn't pair these password fields with the
              table's search box (formless inputs are grouped as one login form) */}
          <form className="space-y-4 py-4" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-1.5">
              <Label htmlFor="new_password">New Password <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Input id="new_password" name="new_password" type={showNewPassword ? "text" : "password"} value={passwordForm.new_password} onChange={handlePasswordInputChange} placeholder="Enter new password" className={`autofill-fix pr-10 ${passwordErrors.new_password ? "border-destructive" : ""}`} autoComplete="new-password" />
                <button type="button" onClick={() => setShowNewPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.new_password && <p className="text-xs text-destructive">{passwordErrors.new_password}</p>}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="confirm_password">Confirm Password <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Input id="confirm_password" name="confirm_password" type={showConfirmPassword ? "text" : "password"} value={passwordForm.confirm_password} onChange={handlePasswordInputChange} placeholder="Confirm new password" className={`autofill-fix pr-10 ${passwordErrors.confirm_password ? "border-destructive" : ""}`} autoComplete="new-password" />
                <button type="button" onClick={() => setShowConfirmPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.confirm_password && <p className="text-xs text-destructive">{passwordErrors.confirm_password}</p>}
            </div>
          </form>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={handleClosePasswordDialog} className="w-full sm:w-auto">Cancel</Button>
            <Button type="button" onClick={handlePasswordChange} disabled={submitting} className="w-full sm:w-auto">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Changing...</> : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ── */}
{/* ── Delete Dialog ── */}
<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
  <DialogContent className="w-[95vw] max-w-md p-4">
    <DialogHeader className="pb-1">
      <DialogTitle className="text-destructive text-base">Delete User</DialogTitle>
      <DialogDescription className="text-sm">
        Are you sure you want to delete this user? This action cannot be undone.
      </DialogDescription>
    </DialogHeader>
    
    {/* User Details */}
    <div className="py-2 px-3 border rounded-lg bg-muted/30">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Full Name</span>
          <span className="text-sm font-medium">
            {users.find(u => u.user_id === selectedUserId)?.full_name || 'Unknown User'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Username</span>
          <span className="text-sm">
            @{users.find(u => u.user_id === selectedUserId)?.username || 'N/A'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Email</span>
          <span className="text-sm  max-w-[180px] text-right">
            {users.find(u => u.user_id === selectedUserId)?.email || '—'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Role</span>
          <span className="text-sm">
            {users.find(u => u.user_id === selectedUserId)?.role_name || '—'}
          </span>
        </div>
        <div className="flex items-center justify-between pt-1 border-t">
          <span className="text-xs text-muted-foreground">Last Login</span>
          <span className="text-sm">
            {users.find(u => u.user_id === selectedUserId)?.last_login 
              ? formatDateTime(users.find(u => u.user_id === selectedUserId).last_login) 
              : 'Never'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Status</span>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            users.find(u => u.user_id === selectedUserId)?.status === "active" 
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" 
              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
          }`}>
            {users.find(u => u.user_id === selectedUserId)?.status || '—'}
          </span>
        </div>
      </div>
    </div>
    
    <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
      <Button 
        type="button" 
        variant="outline" 
        onClick={() => setDeleteDialogOpen(false)} 
        className="w-full sm:w-auto h-8 text-sm"
      >
        Cancel
      </Button>
      <Button 
        type="button" 
        onClick={handleDeleteConfirm} 
        disabled={submitting} 
        className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90 h-8 text-sm"
      >
        {submitting ? (
          <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Deleting...</>
        ) : (
          "Delete User"
        )}
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
    </>
  );
};

export default UserManagementPage;